import { ENV } from '@app/constants/env';
import { DocumentUploadEntity } from '@feature/types/document.upload.type';
import { LinkedJobFormEntity } from '@feature/types/linkjob.forms.type';
import { FormEntityforOrderSummery } from '@feature/types/ordersummery.form.type';
import { PrealertGridEntity, PreAlertRSIServiceGridEntity } from '@feature/types/pre.alert.type.grid';
import { PrealertDetailsEntity } from '@feature/types/prealert.details.type';
import { TrafficeOrderSummeryGridDto } from '@feature/types/traffic.ordersummery.grid.type';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { getToken } from '@app/helpers/authProvider';
import { UnlinkSlotRequest } from '@feature/types/job.formlink.type';
import { PrealertTemplateData } from '@feature/types/prealert.template.data.type';
// features/EfairsJob/api/eFairsJobSliceApi.ts

// Adjust fields to match your backend WorkOrder DTO
export interface WorkOrder {
    id: number;
    title: string;
    description?: string;
    status?: string;
    createdAt?: string;
    updatedAt?: string;
    // add any other fields...
}

export interface CreateWorkOrderRequest extends Omit<WorkOrder, 'id' | 'createdAt' | 'updatedAt'> {}
export interface CreateRSIRequest extends Omit<DocumentUploadEntity, 'id' | 'createdAt' | 'updatedAt'> {}
export interface UpdateWorkOrderRequest extends Partial<CreateWorkOrderRequest> {
    id: number;
}
export interface LinkWorkOrderRequest extends Omit<LinkedJobFormEntity, 'id' | 'createdAt' | 'updatedAt'> {}


// Request model you said must be sent
export interface WorkOrderListRequest {
    UserID: number;
    Mstr_Show_ID: number;
    PageNumber: number;
    PageSize: number;
    TotalRecord?: number;   // usually returned from server, keep optional on request
    Row_Number?: number;
    filtered?: string;
    SortColumn?: string;
    SortOrder?: string;
    WOType?: string;       // Added WOType parameter
}

// If API returns paging metadata, adjust this:
export interface WorkOrderListResponse {
    data: WorkOrder[];          // or items / results depending on API
    totalRecord?: number;
    pageNumber?: number;
    pageSize?: number;
}

// Prepare body for POST request instead of query string
export const buildWorkOrderListBody = (o: WorkOrderListRequest) => ({
    UserID: o.UserID,
    Mstr_Show_ID: o.Mstr_Show_ID,
    PageNumber: o.PageNumber,
    PageSize: o.PageSize,
    filtered: o.filtered,
    SortColumn: o.SortColumn,
    SortOrder: o.SortOrder,
    TotalRecord: o.TotalRecord,
    Row_Number: o.Row_Number,
});

// Update getWorkOrders endpoint to:
// getWorkOrders: builder.query<WorkOrderListResponse | WorkOrder[], WorkOrderListRequest>({
//   query: (arg) => ({
//     url: 'WorkOrder/GetAllWorkOrder',
//     method: 'POST',
//     body: buildWorkOrderListBody(arg),
//   }),
//   ...
// });

const API_BASE_URL = ENV.BASE_API_URL +ENV.JOB_APIURL;

const rawBaseQuery = fetchBaseQuery({
  baseUrl: API_BASE_URL,
});

const authBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  incomingArgs,
  api,
  extraOptions
) => {
  let args: string | FetchArgs = incomingArgs;

  if (typeof args === 'string') {
    args = { url: args };
  }

  try {
    const token = await getToken();
    if (token) {
      const headers =
        (args as FetchArgs).headers instanceof Headers
          ? (args as FetchArgs).headers
          : new Headers((args as FetchArgs).headers);

      headers.set('Authorization', `Bearer ${token}`);
      (args as FetchArgs).headers = headers;
    }

    const result = await rawBaseQuery(args, api, extraOptions);

    if (result.error && result.error.status === 401 && token) {
      // refresh token flow could go here
    }

    return result;
  } catch {
    return { error: { status: 500, data: 'Token retrieval failed' } } as any;
  }
};

export const eFairsJobSliceApi = createApi({
    reducerPath: 'eFairsJobSliceApi',
    baseQuery: authBaseQuery,
    tagTypes: ['WorkOrder', 'GetAllWorkOrder','Traffic'],
    endpoints: (builder) => ({
        // POST like the provided useEffect example
        getWorkOrders: builder.query<WorkOrderListResponse | WorkOrder[], WorkOrderListRequest>({
            query: (arg) => ({
                url: 'WorkOrder/GetAllWorkOrder',
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: {
                    userID: arg.UserID,
                    mstr_Show_ID: arg.Mstr_Show_ID,
                    pageNumber: arg.PageNumber,
                    pageSize: arg.PageSize,
                    totalRecord: arg.TotalRecord ?? 0,
                    row_Number: arg.Row_Number ?? 0,
                    filtered: arg.filtered ?? '',
                    sortColumn: arg.SortColumn,
                    sortOrder: arg.SortOrder,
                    WOType: arg.WOType ?? 'WO', // Added WOType parameter
                },
            }), 
            transformResponse: (response: any): WorkOrderListResponse | WorkOrder[] => {
                if (Array.isArray(response)) return response;
                return response;
            },
            providesTags: (result) => {
                const list: WorkOrder[] =
                    Array.isArray(result) ? result : (result?.data ?? []);
                return list.length
                    ? [
                          ...list.map(w => ({ type: 'WorkOrder' as const, id: w.id })),
                          { type: 'GetAllWorkOrder', id: 'LIST' },
                      ]
                    : [{ type: 'GetAllWorkOrder', id: 'LIST' }];
            }, 
        }),
        getWorkOrder: builder.query<WorkOrder, number>({
            query: (id) => `WorkOrder/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        createWorkOrder: builder.mutation<WorkOrder, CreateWorkOrderRequest>({
            query: (body) => ({
                url: 'WorkOrder',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'GetAllWorkOrder', id: 'LIST' }],
        }),
        updateWorkOrder: builder.mutation<WorkOrder, UpdateWorkOrderRequest>({
            query: ({ id, ...patch }) => ({
                url: `WorkOrder/${id}`,
                method: 'PUT',
                body: patch,
            }),
            invalidatesTags: (result, _err, { id }) => [
                { type: 'WorkOrder', id },
                { type: 'GetAllWorkOrder', id: 'LIST' },
            ],
        }),
        deleteWorkOrder: builder.mutation<boolean, { workOrderId: number; userid: number; showid: number }>({
            query: ({ workOrderId, userid, showid }) => ({
                url: `WorkOrder/DeleteWorkOrder/${workOrderId}/${userid}/${showid}`,
                method: 'PUT',
            }),
            invalidatesTags: (result, _err, { workOrderId }) => [
                { type: 'WorkOrder', id: workOrderId },
                { type: 'GetAllWorkOrder', id: 'LIST' },
            ],
        }),
        getPreAlertData: builder.query<PrealertGridEntity[], string>({
            query: (id) => `WorkOrder/GetPreAlertData/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        
        getPreAlertRSIServiceData: builder.query<PreAlertRSIServiceGridEntity[], string>({
            query: (id) => `WorkOrder/GetPreAlertRSIServiceData/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        getPreAlertDetailsData: builder.query<PrealertDetailsEntity[], string>({
            query: (id) => `WorkOrder/GetPreAlertDetailsData/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
         getPreAlertTemplateDataById: builder.query<PrealertTemplateData[], number>({
            query: (id) => `WorkOrder/GetPreAlertTemplateDataById/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        createRSIData: builder.mutation<DocumentUploadEntity, CreateRSIRequest>({
            query: (body) => ({
                url: 'DocumentUpload/CreateDocumentUpload',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'WorkOrder', id: 'LIST' }],
        }),
        getOrderSummeryFormData: builder.query<FormEntityforOrderSummery[], number>({
            query: (id) => `WorkOrder/GetFormDataforOrderSummary/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        getTrafficOrderSummeryFormData: builder.query<TrafficeOrderSummeryGridDto[], number>({
            query: (id) => `WorkOrder/GetTrafficDataforOrderSummary/${id}`,
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),
        linkWorkOrderToSlot: builder.mutation<LinkedJobFormEntity, LinkWorkOrderRequest>({
            query: (body) => ({
                url: 'Traffic/LinkTrafficJobBookedSlot',
                method: 'POST',
                body,
            }),
            invalidatesTags: [{ type: 'Traffic', id: 'LIST' }],
        }),
        getSlotDetailsForQuestionnaireForm: builder.query<TrafficeOrderSummeryGridDto[], { show_id: number }>({
            query: ({ show_id }) => `Traffic/GetSlotDetailsForBookingGrid?showId=${show_id}`,
            providesTags: (result, _err, { show_id }) => [{ type: 'Traffic', id: show_id }],
        }),
        getWOServiceDataForRSI: builder.query<PrealertDetailsEntity[], number>({
            query: (serviceId) => ({
                url: `WorkOrder/GetWOServiceDataForRSI/${serviceId}`,
                method: 'GET',
            }),
            providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
        }),

unlinkTrafficBookedSlot: builder.mutation<number, UnlinkSlotRequest>({
    query: (body) => ({
        url: 'WorkOrder/UnlinkTrafficBookedSlot',
        method: 'POST',
        body,
    }),
    invalidatesTags: [{ type: 'Traffic', id: 'LIST' }],
}),
unlinkServiceBookedSlot: builder.mutation<string, UnlinkSlotRequest>({
    query: (body) => ({
        url: 'WorkOrder/UnlinkServiceBookedSlot',
        method: 'POST',
        body,
    }),
    invalidatesTags: [{ type: 'Traffic', id: 'LIST' }],
}),

insertPreAlertFileAttachments: builder.mutation<any, {
    FileAttachmentId: number;
    FileAttachments: string;
    PreAlertID: number;
    UserID: number;
}>({
    query: (body) => ({
        url: 'FileAttachments/InsertPreAlertFileAttachments',
        method: 'POST',
        body,
    }),
    invalidatesTags: [{ type: 'WorkOrder', id: 'LIST' }],
}),
getPreAlertFileAttachments: builder.query<any[], {
    FileAttachmentId: number;
    FileAttachments: string;
    PreAlertID: number;
    UserID: number;
}>({
    query: (body) => ({
        url: 'FileAttachments/GetAllPreAlertFileAttachments',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
    }),
    providesTags: [{ type: 'WorkOrder', id: 'LIST' }],
}),

getAllRecipients: builder.query<any, number>({
    query: (id) => ({
        url: `Recipients/GetAllRecipients`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { preAlertId: id },
    }),
    providesTags: (result, _err, id) => [{ type: 'WorkOrder', id }],
}),

        
    }),
});

export const {
    useGetWorkOrdersQuery,
    useGetWorkOrderQuery,
    useCreateWorkOrderMutation,
    useUpdateWorkOrderMutation,
    useDeleteWorkOrderMutation,
    useGetPreAlertDataQuery,
    useGetPreAlertRSIServiceDataQuery,
    useGetPreAlertDetailsDataQuery,
    useCreateRSIDataMutation,
    useGetOrderSummeryFormDataQuery,
    useGetTrafficOrderSummeryFormDataQuery,
    useLinkWorkOrderToSlotMutation,
    useGetSlotDetailsForQuestionnaireFormQuery,
    useGetWOServiceDataForRSIQuery,
    useUnlinkTrafficBookedSlotMutation,
    useUnlinkServiceBookedSlotMutation,
    useInsertPreAlertFileAttachmentsMutation,
    useGetPreAlertFileAttachmentsQuery,
    useGetAllRecipientsQuery,
    useGetPreAlertTemplateDataByIdQuery,
} = eFairsJobSliceApi;

// Add this reducer + middleware in your store:
// import { eFairsJobSliceApi } from '.../eFairsJobSliceApi';
// reducer: { [eFairsJobSliceApi.reducerPath]: eFairsJobSliceApi.reducer, ... }
// middleware: (gDM) => gDM().concat(eFairsJobSliceApi.middleware)